import {Component, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {ConfirmationService} from 'primeng/api';
import {Subscription} from 'rxjs';
import {ObjectBaseComponent} from 'features/core/shared/object/mvs-object-base/object-base.component';
import {MvsCoreService} from 'features/core/shared/service/mvs-core.service';
import {MvsMessageService} from 'features/core/shared/service/message/mvs-message.service';
import {ObserverService} from 'features/core/shared/object/service/observer.service';
import {NavigationItem} from 'features/core/shared/dto/navigation/navigation-item';
import {WidgetData} from 'features/core/shared/widget/widget-data';
import {WidgetFactory} from 'features/core/shared/widget/service/widget.factory';
import {WidgetDataParam} from 'features/core/shared/widget/widget-data-param';
import {WidgetForm} from 'features/core/shared/widget/widget-form';
import {WidgetConfigFormLayout} from 'features/core/shared/widget/widget-config-form-layout';
import {WidgetConfigFormField} from 'features/core/shared/widget/widget-config-form-field';
import {QlQueryDto} from 'features/core/shared/ql/data/dto/ql-query.dto';
import {FilterCriteria} from 'features/core/shared/filter/api/filter.criteria';
import {Sorting} from 'features/core/shared/misc/sorting';
import {cnStatusSeverity, CnTagSeverity} from '../../../shared/cn-status-color.helper';
import {
    commissionStatementStatusLabel,
    commissionStatementStatusName
} from '../../../model/protected-domain/enum/commission-statement-status/commission-statement-status.enum';

/**
 * AI:
 * Status: 'in progress'
 * Type: Component
 * SubType: CrudIntegration
 * Reason: Detail OBC for cn.CommissionStatement. Renders the framework-loaded statement (this.dto)
 *         header under Übersicht, the Zeilen as a curated mvs-widget table, and the linked DM
 *         document via the framework mvs-pdf-viewer. Navigation is driven by the framework
 *         activeNavigationItems and supports ?navItem= deep-linking.
 */

/**
 * Object component for cn.CommissionStatement.
 *
 * The framework {@link ObjectBaseComponent} loads the CommissionStatement entity into this.dto from
 * the standard CRUD endpoint GET /mvsa/cn/commissionStatements/{id}; this.dto therefore carries every
 * header field the Übersicht renders (partnerDtoName / vermittlerNr / kontoNr / kontoauszugNr /
 * statementDate / oldBalance / newBalance / currency / status ordinal / dmDocument). There is NO
 * custom detail fetch — the previous getStatementDetail() machinery was removed.
 *
 * Navigation (framework activeNavigationItems, G6) — Übersicht / Zeilen / Dokument:
 *   - Übersicht: read-only statement header fields off this.dto,
 *   - Zeilen:    a curated mvs-widget table over cn.CommissionStatementLine filtered by its statement
 *                FK (the rich business columns are shown — VSNR / Tarif / Buchungsperiode /
 *                Prov.-Zeitraum von+bis / Hauptfälligkeit / Prov.berechtigter Betrag / Provisionssatz /
 *                Betrag / Zahlweise / Auslöser / Vertrag),
 *   - Dokument:  the linked DM document previewed via the framework mvs-pdf-viewer.
 *
 * NOTE — no "Buchungen" tab: there is no booking entity. Booking entries (Buchungen) are a purely
 * computed view (CnImportEntryViewDto), grouped by belegNr, returned only by the import endpoint —
 * there is NO CommissionStatementEntry / booking JPA entity in the cn module. A real grouped-bookings
 * view would need its own backend entity / QL view (out of scope), so the tab was removed rather than
 * duplicating the Zeilen table over the same cn.CommissionStatementLine entity.
 *
 * The navigation items are emitted via onNavigationItems so a hosting object page / shell can render
 * them; the in-template nav control drives section switching when the OBC is embedded directly (e.g.
 * the Eingangsfreigabe master-detail panel). ?navItem= deep-linking is supported (G6.5).
 *
 * G2.4 / G2.5: onObjectChanged() is implemented and never calls refreshObject().
 */
@Component({
    selector: 'mvs-cn-commission-statement-object-component',
    templateUrl: './cn-commission-statement-object.component.html',
    styleUrls: ['./cn-commission-statement-object.component.scss'],
    standalone: false
})
export class CnCommissionStatementObjectComponent
    extends ObjectBaseComponent
    implements OnInit, OnChanges, OnDestroy {

    /** Navigation action ids — kept as constants so TS and template never drift apart. */
    static readonly NAV_OVERVIEW = 'overview';
    static readonly NAV_LINES = 'lines';
    static readonly NAV_DOCUMENT = 'document';

    /** Navigation sections for this OBC (Übersicht / Zeilen / Dokument). */
    navigationItems: NavigationItem[] = [
        {
            label: 'Übersicht',
            action: CnCommissionStatementObjectComponent.NAV_OVERVIEW,
            icon: 'fa-regular fa-circle-info',
            tooltip: 'Kopfdaten des Kontoauszugs',
            default: true
        },
        {
            label: 'Zeilen',
            action: CnCommissionStatementObjectComponent.NAV_LINES,
            icon: 'fa-regular fa-table-list',
            tooltip: 'Provisionszeilen'
        },
        {
            label: 'Dokument',
            action: CnCommissionStatementObjectComponent.NAV_DOCUMENT,
            icon: 'fa-regular fa-file-pdf',
            tooltip: 'Hinterlegtes Dokument'
        }
    ];

    /** Server-paged Zeilen widget (curated columns over cn.CommissionStatementLine, by statement FK). */
    linesWidget: WidgetData | null = null;

    /** Subscription to the ?navItem= query param (G6.5); torn down on destroy. */
    private queryParamSubscription: Subscription | null = null;

    constructor(
        protected override coreService: MvsCoreService,
        protected override messageService: MvsMessageService,
        protected override confirmationService: ConfirmationService,
        protected override observerService: ObserverService,
        protected route: ActivatedRoute
    ) {
        super(coreService, messageService, confirmationService, observerService);
    }

    override ngOnInit(): void {
        super.ngOnInit();
        this.subscribeToNavItemQueryParam();
    }

    /**
     * Post-load hook (G2.4). The base has loaded this.dto (the CommissionStatement entity). We set
     * the page title from the Kontoauszug-Nr, default the active navigation item (only when not
     * already set by ?navItem=), emit the navigation items for a hosting shell and build the Zeilen
     * table widget. We never call refreshObject() here (G2.5).
     */
    override onObjectChanged(): void {
        const kontoauszugNr = this.dto?.kontoauszugNr ?? this.objectIdentifier?.objectId ?? '';
        this.setPageTitle(`Kontoauszug ${kontoauszugNr}`.trim());

        // Only default the section when ?navItem= did not already select one (G6.5).
        if (!this.activeNavigationItems) {
            this.activeNavigationItems =
                this.navigationItems.find(item => item.default) ?? this.navigationItems[0];
        }
        this.handleNavigationItems(this.navigationItems);

        this.refreshWidgets();
    }

    /** Switch the active navigation section (in-template nav control). */
    selectNav(item: NavigationItem): void {
        this.activeNavigationItems = item;
    }

    /**
     * Subscribe to the ?navItem= URL query param and switch the active section to the matching
     * navigation item (G6.5). ObjectBaseComponent does not do this itself, so the OBC adds it
     * manually rather than switching base class.
     */
    private subscribeToNavItemQueryParam(): void {
        this.queryParamSubscription = this.route.queryParams.subscribe(params => {
            const navItemAction = params['navItem'] && decodeURIComponent(params['navItem']);
            if (!navItemAction || !this.navigationItems?.length) {
                return;
            }
            const found = this.navigationItems.find(item => item.action === navItemAction);
            if (found) {
                this.activeNavigationItems = found;
            }
        });
    }

    // =====================================================================================
    // Zeilen table widget (curated columns)
    // =====================================================================================

    /**
     * AI:
     * Status: 'in progress'
     * Type: Widget
     * SubType: TableWidget (createWidgetTableQl + WidgetForm custom layout)
     * Reason: Build the curated Zeilen table over cn.CommissionStatementLine, filtered by the statement
     *         FK (e.commissionStatementId = this.dto.id). Uses the createWidgetTableQl path with an
     *         explicit QL projection + WidgetForm so ONLY business-meaningful columns are shown
     *         (the rich commission-line set: VSNR / Tarif / Buchungsperiode / Prov.-Zeitraum von+bis /
     *         Hauptfälligkeit / Prov.berechtigter Betrag / Provisionssatz / Betrag / Zahlweise /
     *         Auslöser / Vertrag). The fkeyResolve path does
     *         NOT honor a WidgetForm layout (SKILL.md TW-3), so it leaked the entity's default audit
     *         columns (Angelegt von, Ablauf, Provisionsabrechnung-ID, …) — mirrors the model-picker fix
     *         in CnReconciliationPanelComponent.
     */
    private refreshWidgets(): void {
        const statementId = this.dto?.id ?? this.objectIdentifier?.objectId;
        if (statementId == null) {
            return;
        }

        this.linesWidget = WidgetFactory.createWidgetTableQl(
            'cn.commissionStatement.lines.table.main',
            'Zeilen',
            CnCommissionStatementObjectComponent.buildLinesQlQuery(statementId),
            WidgetDataParam.create('widgetForm', CnCommissionStatementObjectComponent.buildLinesWidgetForm())
        );
    }

    /**
     * Explicit QL projection for the curated Zeilen table. Only the columns the WidgetForm exposes are
     * selected off the root entity `e`; the persisted audit fields are never projected. Filtered by the
     * statement FK (e.commissionStatementId) so only this statement's lines are returned.
     */
    private static buildLinesQlQuery(statementId: number): QlQueryDto {
        return {
            name: 'cn.commissionStatement.lines.table.main',
            type: 'view',
            start: {
                name: 'cn.CommissionStatementLine',
                as: 'e',
                type: 'entity',
                fields: [
                    {name: 'id'},
                    {name: 'vsnr'},                    // VSNR
                    {name: 'tariffName'},              // Tarif
                    {name: 'bookingPeriod'},           // Buchungsperiode
                    {name: 'provPeriodFrom'},          // Prov.-Zeitraum von (NEW field)
                    {name: 'provPeriodTo'},            // Prov.-Zeitraum bis (NEW field)
                    {name: 'mainDueDate'},             // Hauptfälligkeit (NEW field)
                    {name: 'provBerechtigterBetrag'},  // Prov.berechtigter Betrag (money)
                    {name: 'provisionsSatz'},          // Provisionssatz
                    {name: 'provisionAmount'},         // Betrag (money)
                    {name: 'zahlweise'},               // Zahlweise
                    {name: 'ausloeser'},               // Auslöser (Trigger enum)
                    {name: 'contractId'},              // Vertrag
                ],
            },
            // Always-on, server-side scope to this statement's lines (fixed, not a runtime filter).
            fixedFilters: [
                FilterCriteria.create('e.commissionStatementId', FilterCriteria.cOperatorEqual, statementId)
            ],
            // e. prefix is mandatory for QL sorting field names on the root content reference.
            sortings: [new Sorting('e.id', true)],
        };
    }

    /**
     * Curated WidgetForm layout for the Zeilen table. Pins the column SET + ORDER and applies explicit
     * German uiLabels. Field ids are the QL result keys (`e.`-prefixed). hideFields=true is the
     * default-layout convention (fields with uiHidden=false are shown).
     */
    private static buildLinesWidgetForm(): WidgetForm {
        const columns: { field: string; uiLabel: string }[] = [
            {field: 'e.vsnr', uiLabel: 'VSNR'},
            {field: 'e.tariffName', uiLabel: 'Tarif'},
            {field: 'e.bookingPeriod', uiLabel: 'Buchungsperiode'},
            {field: 'e.provPeriodFrom', uiLabel: 'Prov.-Zeitraum von'},
            {field: 'e.provPeriodTo', uiLabel: 'Prov.-Zeitraum bis'},
            {field: 'e.mainDueDate', uiLabel: 'Hauptfälligkeit'},
            {field: 'e.provBerechtigterBetrag', uiLabel: 'Prov.berechtigter Betrag'},
            {field: 'e.provisionsSatz', uiLabel: 'Provisionssatz'},
            {field: 'e.provisionAmount', uiLabel: 'Betrag'},
            {field: 'e.zahlweise', uiLabel: 'Zahlweise'},
            {field: 'e.ausloeser', uiLabel: 'Auslöser'},
            {field: 'e.contractId', uiLabel: 'Vertrag'},
        ];

        const layout = new WidgetConfigFormLayout();
        layout.hideFields = true;
        layout.fieldsConfigured = true;

        columns.forEach((column, index) => {
            const field = WidgetConfigFormField.create(column.field, '', '');
            field.uiLabel = column.uiLabel; // explicit German header
            field.uiPriority = index;       // preserves the curated column order
            layout.addField(field);
        });

        const widgetForm = new WidgetForm();
        widgetForm.setDefaultLayout(layout);
        return widgetForm;
    }

    // =====================================================================================
    // Display helpers
    // =====================================================================================

    /** German display label for the current statement status, or '–' when unknown. */
    get statusLabel(): string {
        return commissionStatementStatusLabel(this.dto?.status);
    }

    /**
     * Neutral PrimeNG severity for the statement status badge. The shared {@link cnStatusSeverity}
     * map keys on the English enum NAME, so we resolve the canonical name first (handles both the
     * persisted ordinal and a name source) before the lookup.
     */
    get statusTagSeverity(): CnTagSeverity {
        return cnStatusSeverity(commissionStatementStatusName(this.dto?.status));
    }

    /** The linked DM document id read off the loaded CommissionStatement entity, or null. */
    get dmDocumentId(): number | null {
        return this.dto?.dmDocument ?? null;
    }

    /** Whether a DM document is attached to the statement. */
    get hasDocument(): boolean {
        return this.dmDocumentId != null;
    }

    override ngOnChanges(changes: SimpleChanges): void {
        super.ngOnChanges(changes);
    }

    /** Clean up the query-param subscription (no leaks). */
    override ngOnDestroy(): void {
        this.queryParamSubscription?.unsubscribe();
        super.ngOnDestroy();
    }
}
